import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

import { forwardMailApi, SendResponse } from '../../../services/forwardMailApi';
import RichTextEditor from '../../../components/RichTextEditor';

export default function SendTab() {
  const [domains, setDomains] = useState<string[]>([]);
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [domainError, setDomainError] = useState<string | null>(null);

  const [fromLocal, setFromLocal] = useState('hello');
  const [fromDomain, setFromDomain] = useState('');
  const [fromDisplay, setFromDisplay] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');

  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<SendResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingDomains(true);
    forwardMailApi
      .listDomains()
      .then(r => {
        if (cancelled) return;
        setDomains(r.domains);
        if (r.domains.length && !fromDomain) setFromDomain(r.domains[0]);
      })
      .catch(e => {
        if (!cancelled)
          setDomainError(e?.response?.data?.detail || e.message || 'failed to load domains');
      })
      .finally(() => !cancelled && setLoadingDomains(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSubmit =
    !!fromLocal.trim() &&
    !!fromDomain &&
    !!to.trim() &&
    !!subject.trim() &&
    !!bodyHtml.trim() &&
    !sending;

  const handleSend = async () => {
    setError(null);
    setSuccess(null);
    setSending(true);
    try {
      const payload = {
        from_local_part: fromLocal.trim(),
        from_domain: fromDomain,
        from_display: fromDisplay.trim() || null,
        to: to.trim(),
        cc: cc.trim() || null,
        bcc: bcc.trim() || null,
        reply_to: replyTo.trim() || null,
        subject: subject.trim(),
        body_text: null,
        body_html: bodyHtml,
      };
      const resp = await forwardMailApi.send(payload);
      setSuccess(resp);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'send failed');
    } finally {
      setSending(false);
    }
  };

  const handleClear = () => {
    setTo('');
    setCc('');
    setBcc('');
    setReplyTo('');
    setSubject('');
    setBodyHtml('');
    setSuccess(null);
    setError(null);
  };

  return (
    <Stack spacing={2}>
      {domainError && <Alert severity="error">{domainError}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          Sent as <b>{success.from}</b> — message id{' '}
          <code>{success.message_id ?? '(unknown)'}</code>
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          From
        </Typography>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField
              label="Local part"
              fullWidth
              size="small"
              value={fromLocal}
              onChange={e => setFromLocal(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">@</InputAdornment>,
              }}
              helperText="letters, digits, . _ % + - only"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <Select
                value={fromDomain}
                onChange={e => setFromDomain(e.target.value)}
                disabled={loadingDomains || domains.length === 0}
                displayEmpty
              >
                {domains.map(d => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Display name (optional)"
              fullWidth
              size="small"
              value={fromDisplay}
              onChange={e => setFromDisplay(e.target.value)}
              placeholder='e.g. "Sales Team"'
            />
          </Grid>
        </Grid>
      </Box>

      <TextField
        label="To"
        fullWidth
        size="small"
        value={to}
        onChange={e => setTo(e.target.value)}
        helperText="One or more addresses, separated by commas, semicolons, or newlines"
      />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Cc"
            fullWidth
            size="small"
            value={cc}
            onChange={e => setCc(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Bcc"
            fullWidth
            size="small"
            value={bcc}
            onChange={e => setBcc(e.target.value)}
          />
        </Grid>
      </Grid>

      <TextField
        label="Reply-To (optional)"
        fullWidth
        size="small"
        value={replyTo}
        onChange={e => setReplyTo(e.target.value)}
        helperText="Defaults to the From address if blank"
      />
      <TextField
        label="Subject"
        fullWidth
        size="small"
        value={subject}
        onChange={e => setSubject(e.target.value)}
      />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Body
        </Typography>
        <RichTextEditor
          value={bodyHtml}
          onChange={setBodyHtml}
          placeholder="Compose your message…"
        />
      </Box>

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button onClick={handleClear} disabled={sending}>
          Clear
        </Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
          onClick={handleSend}
          disabled={!canSubmit}
        >
          Send
        </Button>
      </Stack>
    </Stack>
  );
}
